import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import type {
  AnalyticsUntrackedJoin,
  AnalyticsUntrackedJoinSource,
  PaginatedResponse,
} from "../../../api.ts";
import { ADMIN_LIST_PAGE_SIZE, adminGet } from "../../../api.ts";
import { AdminPageHeader } from "../../../components/AdminPageChrome.tsx";
import { CursorPagination } from "../../../components/CursorPagination.tsx";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { StatsTrendControls, type TrendRange } from "../../../components/StatsTrendControls.tsx";
import { BusinessDateTime } from "../../../components/BusinessDateTime.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import {
  UNTRACKED_JOIN_SOURCE_COLORS,
  UNTRACKED_JOIN_SOURCE_LABELS,
  type UntrackedJoinsNavFilters,
  untrackedJoinSourceLabel,
} from "./untracked-join-shared.ts";

const PAGE_SIZE = ADMIN_LIST_PAGE_SIZE;

const VALID_SOURCES = new Set<AnalyticsUntrackedJoinSource>([
  "no_invite_link",
  "fallback_link",
  "unmatched_link",
  "direct_join",
]);

const parseJoinSource = (raw: string | null): AnalyticsUntrackedJoinSource | "all" => {
  if (raw === null || raw === "") return "all";
  return VALID_SOURCES.has(raw as AnalyticsUntrackedJoinSource)
    ? (raw as AnalyticsUntrackedJoinSource)
    : "all";
};

interface ChatOption {
  readonly chatId: number;
  readonly title: string | null;
}

interface ChatsResponse {
  readonly items: ReadonlyArray<ChatOption>;
}

interface StatsSummary {
  readonly untrackedJoinBreakdown: ReadonlyArray<{
    readonly source: string;
    readonly count: number;
  }>;
  readonly untrackedJoinTopLinks: ReadonlyArray<{
    readonly inviteLinkKey: string;
    readonly joinSource: string;
    readonly count: number;
  }>;
}

type Props = {
  readonly botId: string;
  readonly timezone: string;
  readonly onOpenStats?: () => void;
};

export function UntrackedJoinsTab(props: Props) {
  const { botId, timezone, onOpenStats } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const [sourceFilter, setSourceFilter] = useState<AnalyticsUntrackedJoinSource | "all">(() =>
    parseJoinSource(searchParams.get("joinSource")),
  );
  const [chatId, setChatId] = useState<string | null>(() => searchParams.get("chatId"));
  const [linkKeyInput, setLinkKeyInput] = useState(() => searchParams.get("inviteLinkKey") ?? "");
  const [debouncedLinkKey] = useDebouncedValue(linkKeyInput, 300);
  const [range, setRange] = useState<TrendRange>("today");
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = cursors[pageIndex] ?? null;

  const resetPages = () => {
    setCursors([null]);
    setPageIndex(0);
  };

  const syncUrl = (next: {
    joinSource: AnalyticsUntrackedJoinSource | "all";
    chatId: string | null;
    inviteLinkKey: string;
  }) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "analytics-untracked-joins");
        if (next.joinSource === "all") params.delete("joinSource");
        else params.set("joinSource", next.joinSource);
        if (next.chatId === null || next.chatId === "") params.delete("chatId");
        else params.set("chatId", next.chatId);
        if (next.inviteLinkKey.trim() === "") params.delete("inviteLinkKey");
        else params.set("inviteLinkKey", next.inviteLinkKey.trim());
        return params;
      },
      { replace: true },
    );
  };

  const urlJoinSource = searchParams.get("joinSource");
  const urlChatId = searchParams.get("chatId");
  const urlInviteLinkKey = searchParams.get("inviteLinkKey") ?? "";

  useEffect(() => {
    const nextSource = parseJoinSource(urlJoinSource);
    if (nextSource !== sourceFilter) setSourceFilter(nextSource);
    if (urlChatId !== chatId) setChatId(urlChatId);
    if (urlInviteLinkKey !== linkKeyInput) setLinkKeyInput(urlInviteLinkKey);
    resetPages();
  }, [urlJoinSource, urlChatId, urlInviteLinkKey]);

  const applyFilters = (next: {
    joinSource?: AnalyticsUntrackedJoinSource | "all";
    chatId?: string | null;
    inviteLinkKey?: string;
  }) => {
    const mergedSource = next.joinSource ?? sourceFilter;
    const mergedChatId = next.chatId !== undefined ? next.chatId : chatId;
    const mergedLinkKey = next.inviteLinkKey !== undefined ? next.inviteLinkKey : linkKeyInput;
    setSourceFilter(mergedSource);
    setChatId(mergedChatId);
    setLinkKeyInput(mergedLinkKey);
    resetPages();
    syncUrl({
      joinSource: mergedSource,
      chatId: mergedChatId,
      inviteLinkKey: mergedLinkKey,
    });
  };

  const chatsQuery = useQuery({
    queryKey: ["analytics", botId, "chats"],
    queryFn: () => adminGet<ChatsResponse>(`/analytics/${botId}/chats`),
    staleTime: 60_000,
  });

  const chatOptions = (chatsQuery.data?.items ?? []).map((c) => ({
    value: String(c.chatId),
    label: c.title === null || c.title === "" ? String(c.chatId) : `${c.title} (${c.chatId})`,
  }));

  const statsSummary = useQuery({
    queryKey: ["analytics", botId, "untracked-summary", chatId],
    queryFn: () => {
      const qs = chatId === null ? "" : `&chatId=${encodeURIComponent(chatId)}`;
      return adminGet<StatsSummary>(
        `/analytics/${botId}/stats?range=today&granularity=hour&sections=untracked${qs}`,
      );
    },
    select: (data) => ({
      breakdown: data.untrackedJoinBreakdown,
      topLinks: data.untrackedJoinTopLinks,
      total: data.untrackedJoinBreakdown.reduce((sum, it) => sum + it.count, 0),
    }),
    staleTime: 30_000,
  });

  const list = useQuery({
    queryKey: [
      "bot",
      botId,
      "analytics-untracked-joins",
      range,
      sourceFilter,
      chatId,
      debouncedLinkKey,
      cursor,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("pageSize", String(PAGE_SIZE));
      params.set("range", range);
      if (cursor !== null) params.set("cursor", cursor);
      if (sourceFilter !== "all") params.set("joinSource", sourceFilter);
      if (chatId !== null && chatId !== "") params.set("chatId", chatId);
      if (debouncedLinkKey.trim() !== "") params.set("inviteLinkKey", debouncedLinkKey.trim());
      return adminGet<PaginatedResponse<AnalyticsUntrackedJoin>>(
        `/analytics/${botId}/untracked-joins?${params.toString()}`,
      );
    },
  });

  const applyNavFilters = (filters: UntrackedJoinsNavFilters) => {
    applyFilters({
      ...(filters.joinSource !== undefined ? { joinSource: filters.joinSource } : {}),
      ...(filters.chatId !== undefined ? { chatId: filters.chatId } : {}),
      ...(filters.inviteLinkKey !== undefined ? { inviteLinkKey: filters.inviteLinkKey } : {}),
    });
  };

  return (
    <Stack gap="md">
      <AdminPageHeader
        title="未归因入群"
        description="进群邀请链不在广告池/per-ad 链内的记录。兜底链与外发链不上报广告，用于排查「群在涨但广告无转化」。"
        actions={
          <Group gap="xs" wrap="wrap">
            <BotTimezoneNotice timezone={timezone} range={range} />
            {onOpenStats !== undefined ? (
              <Button variant="subtle" size="xs" onClick={onOpenStats}>
                返回报表
              </Button>
            ) : null}
          </Group>
        }
      />

      {statsSummary.isLoading ? (
        <Skeleton h={72} radius="md" />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="sm">
          <Card withBorder padding="md">
            <Text size="xs" c="dimmed">
              业务今日合计
            </Text>
            <Text fw={700} size="xl">
              {(statsSummary.data?.total ?? 0).toLocaleString()}
            </Text>
          </Card>
          {(["no_invite_link", "fallback_link", "unmatched_link", "direct_join"] as const).map(
            (source) => {
              const count =
                statsSummary.data?.breakdown.find((it) => it.source === source)?.count ?? 0;
              return (
                <Card
                  key={source}
                  withBorder
                  padding="md"
                  style={{ cursor: "pointer" }}
                  onClick={() => applyNavFilters({ joinSource: source })}
                >
                  <Text size="xs" c="dimmed">
                    {UNTRACKED_JOIN_SOURCE_LABELS[source]}
                  </Text>
                  <Group gap="xs">
                    <Text fw={700} size="xl">
                      {count.toLocaleString()}
                    </Text>
                    <Badge color={UNTRACKED_JOIN_SOURCE_COLORS[source]} variant="light" size="sm">
                      筛选
                    </Badge>
                  </Group>
                </Card>
              );
            },
          )}
        </SimpleGrid>
      )}

      <Group grow align="flex-end">
        <Select
          label="目标群组"
          placeholder="全部群组"
          data={chatOptions}
          value={chatId}
          onChange={(v) => applyFilters({ chatId: v })}
          searchable
          clearable
          nothingFoundMessage="无匹配群组"
          disabled={chatsQuery.isLoading}
        />
        <Select
          label="来源类型"
          data={[
            { label: "全部", value: "all" },
            { label: UNTRACKED_JOIN_SOURCE_LABELS.fallback_link, value: "fallback_link" },
            { label: UNTRACKED_JOIN_SOURCE_LABELS.unmatched_link, value: "unmatched_link" },
            { label: UNTRACKED_JOIN_SOURCE_LABELS.no_invite_link, value: "no_invite_link" },
            { label: UNTRACKED_JOIN_SOURCE_LABELS.direct_join, value: "direct_join" },
          ]}
          value={sourceFilter}
          onChange={(v) => applyFilters({ joinSource: parseJoinSource(v) })}
        />
        <TextInput
          label="链接 key 筛选"
          placeholder="如 t.me/+AbCd"
          value={linkKeyInput}
          onChange={(e) => setLinkKeyInput(e.currentTarget.value)}
          onBlur={() => applyFilters({ inviteLinkKey: linkKeyInput })}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters({ inviteLinkKey: linkKeyInput });
          }}
        />
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            时间
          </Text>
          <StatsTrendControls
            range={range}
            granularity="hour"
            onRangeChange={(r) => {
              setRange(r);
              resetPages();
            }}
            onGranularityChange={() => {}}
            hideGranularity
          />
        </Stack>
      </Group>

      {(statsSummary.data?.topLinks.length ?? 0) > 0 ? (
        <Card withBorder padding="md">
          <Text fw={600} mb="sm">
            业务今日高频链接（点击可筛选）
          </Text>
          <AdminTable minWidth={700}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>链接 key</Table.Th>
                <Table.Th>来源</Table.Th>
                <Table.Th>次数</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {statsSummary.data?.topLinks.map((row) => (
                <Table.Tr
                  key={`${row.inviteLinkKey}-${row.joinSource}`}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    applyNavFilters({
                      inviteLinkKey: row.inviteLinkKey,
                      joinSource: row.joinSource as AnalyticsUntrackedJoinSource,
                    })
                  }
                >
                  <Table.Td>
                    <Code>{row.inviteLinkKey}</Code>
                  </Table.Td>
                  <Table.Td>{untrackedJoinSourceLabel(row.joinSource)}</Table.Td>
                  <Table.Td>{row.count.toLocaleString()}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </AdminTable>
        </Card>
      ) : null}

      <Card withBorder padding="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>明细列表</Text>
          <Text size="sm" c="dimmed">
            本页 {list.data?.items.length ?? 0} 条
            {list.data?.hasMore === true ? "（还有更多）" : ""}
          </Text>
        </Group>
        {list.isLoading ? (
          <Skeleton height={200} />
        ) : list.isError ? (
          <Text c="red">加载失败</Text>
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <Text c="dimmed">
            当前筛选下暂无记录。部署新代码并跑迁移后才会开始积累；也可放宽群组 / 来源筛选。
          </Text>
        ) : (
          <>
            <AdminTable minWidth={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>时间</Table.Th>
                  <Table.Th>来源</Table.Th>
                  <Table.Th>频道/群聊</Table.Th>
                  <Table.Th>用户</Table.Th>
                  <Table.Th>邀请链</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data?.items.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <BusinessDateTime iso={row.occurredAt} businessTimezone={timezone} />
                    </Table.Td>
                    <Table.Td>
                      <Badge color={UNTRACKED_JOIN_SOURCE_COLORS[row.joinSource]} variant="light">
                        {UNTRACKED_JOIN_SOURCE_LABELS[row.joinSource]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.chatTitle ?? String(row.chatId)}</Text>
                      <Text size="xs" c="dimmed">
                        {row.chatId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Code>{row.tgUserId}</Code>
                    </Table.Td>
                    <Table.Td>
                      {row.inviteLink === null ? (
                        <Text size="sm" c="dimmed">
                          -
                        </Text>
                      ) : (
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={row.inviteLink} multiline maw={360}>
                            <Code>{row.inviteLinkKey ?? row.inviteLink}</Code>
                          </Tooltip>
                          <CopyButton value={row.inviteLink}>
                            {({ copied, copy }) => (
                              <Text
                                size="xs"
                                c={copied ? "teal" : "blue"}
                                onClick={copy}
                                style={{ cursor: "pointer" }}
                              >
                                {copied ? "已复制" : "复制"}
                              </Text>
                            )}
                          </CopyButton>
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </AdminTable>
            <CursorPagination
              pageSize={list.data?.pageSize ?? PAGE_SIZE}
              itemCount={list.data?.items.length ?? 0}
              hasMore={list.data?.hasMore === true}
              canGoPrev={pageIndex > 0}
              loading={list.isFetching}
              onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
              onNext={() => {
                const next = list.data?.nextCursor ?? null;
                if (next === null) return;
                setCursors((prev) => {
                  const head = prev.slice(0, pageIndex + 1);
                  return [...head, next];
                });
                setPageIndex((i) => i + 1);
              }}
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
